import { useReactMutation, useReactQuery } from './common';
import { UseQueryResult } from 'react-query';
import {
  showApiErrorNotification,
  showApiSuccessNotification,
} from '../utils/notifications';
import {
  UseQueryHookReturnType,
  LanguageResponseData,
  CategoryResponseData,
  SubCategoryResponseData,
  UsersListResponseData,
  UseMutationHookReturnType,
  SegmentExperimentResponseData,
  PartnersListResponseData,
  CitiesListResponseData,
  ChannelsResponseData,
  AppConfigResponseData,
  CategoryResponseDataForEU,
  ValuePropResponseType,
  GetLocationsPayloadType,
  FeedSubCategoryResponseData,
  GetLocationsResponseData,
  TagsResponseData,
  SiloCityResponseData,
  SiloStateResponseData,
  SiloAppResponseData,
} from '../types';
import {
  getLanguage,
  getCategory,
  getSubCategory,
  getUsersList,
  assignUser,
  getSegmentExperiments,
  getPartnersList,
  getRejectReasons,
  getChannels,
  getAppConfig,
  getEUCategory,
  getValueProp,
  getSubCategoriesFeed,
  getCitiesList,
  getCityList,
  getStateList,
  getCardTags,
  getSiloCityList,
  getSiloStateList,
  getSiloAppList,
} from '../api/common';
import {
  GET_LANGUAGE,
  GET_CATEGORY,
  GET_SUB_CATEGORY,
  GET_ASSIGNEE_USERS,
  GET_SEGMENT_EXPERIMENTS,
  GET_PARTNERS_LIST,
  GET_CITIES_LIST,
  GET_REJECT_REASONS,
  GET_CHANNELS,
  GET_APP_CONFIG,
  GET_CATEGORY_V2,
  GET_EU_VALUE_PROP,
  GET_CITIES,
  GET_STATES,
  GET_CARD_TAGS,
  GET_SILO_CITIES,
  GET_SILO_STATES,
  GET_SILO_APPS,
} from '../api/constants';
import {
  GET_LANGUAGE_ERROR,
  GET_CATEGORY_ERROR,
  GET_SUB_CATEGORY_ERROR,
  ASSIGN_OWNER_ERROR,
  ASSIGN_OWNER_SUCCESSFUL,
  REMOVE_OWNER_SUCCESSFUL,
  REMOVE_OWNER_ERROR,
  GET_SEGMENT_EXPERIMENTS_ERROR,
  GET_PARTNERS_LIST_ERROR,
  GET_CITIES_LIST_ERROR,
  GET_REJECT_REASONS_ERROR,
  GET_CHANNELS_ERROR,
  GET_APP_CONFIG_ERROR,
  GET_VALUE_PROP_ERROR,
  GET_STATE_LIST_ERROR,
  GET_APP_LIST_ERROR,
  GET_TAGS_ERROR,
} from '../lang/en';
import { ClientError } from '../api/utils';
import { AssignUserData, AssignUserResponseType } from '../types/Home';
import { StringMap } from '../../types';
import { useContext, useMemo } from 'react';
import { SelectedPublisherDataContext, SpaceContext } from '../contexts';
import { GET_CAT_SUBCAT } from '../api/spaces/constants';

export const useGetLanguage = (): UseQueryResult<LanguageResponseData> => {
  const queryResult = useReactQuery<LanguageResponseData>(
    [GET_LANGUAGE],
    () => {
      return getLanguage();
    },
    {
      onError: (error) => {
        showApiErrorNotification(GET_LANGUAGE_ERROR, error.message);
      },
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );
  return {
    ...queryResult,
    data: queryResult,
  } as UseQueryHookReturnType<LanguageResponseData>;
};

export const useGetCategory = (): UseQueryResult<CategoryResponseData> => {
  const { onSpaces } = useContext(SpaceContext);
  const queryResult = useReactQuery<CategoryResponseData>(
    onSpaces ? [GET_CAT_SUBCAT] : [GET_CATEGORY],
    () => {
      return getCategory(onSpaces);
    },
    {
      onError: (error) => {
        showApiErrorNotification(GET_CATEGORY_ERROR, error.message);
      },
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );
  return {
    ...queryResult,
    data: queryResult,
  } as UseQueryHookReturnType<CategoryResponseData>;
};

export const useGetEUCategory =
  (): UseQueryResult<CategoryResponseDataForEU> => {
    const queryResult = useReactQuery<CategoryResponseDataForEU>(
      [GET_CATEGORY_V2],
      () => {
        return getEUCategory();
      },
      {
        onError: (error) => {
          showApiErrorNotification(GET_CATEGORY_ERROR, error.message);
        },
        staleTime: Infinity,
        cacheTime: Infinity,
      },
    );
    return {
      ...queryResult,
      data: queryResult,
    } as UseQueryHookReturnType<CategoryResponseDataForEU>;
  };

export const useGetSubCategorySpaces = (
  categoryId?: string,
): UseQueryResult<SubCategoryResponseData> => {
  const queryResult = useReactQuery<SubCategoryResponseData | undefined>(
    [GET_CAT_SUBCAT, categoryId],
    () => {
      if (categoryId) {
        return getSubCategory(categoryId);
      }
    },
    {
      onError: (error) => {
        showApiErrorNotification(GET_SUB_CATEGORY_ERROR, error.message);
      },
    },
  );
  return {
    ...queryResult,
    data: queryResult,
  } as UseQueryHookReturnType<SubCategoryResponseData>;
};

export const useGetSubCategoriesFeed = (
  categoryId?: string,
): UseQueryResult<FeedSubCategoryResponseData> => {
  const queryResult = useReactQuery<FeedSubCategoryResponseData | undefined>(
    [GET_SUB_CATEGORY, categoryId],
    () => {
      if (categoryId) {
        return getSubCategoriesFeed(categoryId);
      }
    },
    {
      onError: (error) => {
        showApiErrorNotification(GET_SUB_CATEGORY_ERROR, error.message);
      },
    },
  );
  return {
    ...queryResult,
    data: queryResult.data,
  } as UseQueryHookReturnType<FeedSubCategoryResponseData>;
};

export const useGetUsersList = (
  name: string,
): UseQueryResult<UsersListResponseData> => {
  const queryResult = useReactQuery<UsersListResponseData | undefined>(
    [GET_ASSIGNEE_USERS, name],
    () => {
      return getUsersList(name);
    },
    {
      onError: (error) => {
        showApiErrorNotification(GET_ASSIGNEE_USERS, error.message);
      },
    },
  );
  return {
    ...queryResult,
  } as UseQueryHookReturnType<UsersListResponseData>;
};

export const useAssignUser = () => {
  let isAssignUser = true;
  const {
    mutate: assignUserMutate,
    data: mutationResult,
    isLoading: isAssignUserLoading,
  } = useReactMutation<AssignUserResponseType, ClientError, AssignUserData>(
    (params) => {
      if (!params.userId) {
        isAssignUser = false;
      }
      return assignUser(params);
    },
    {
      onError: (error) => {
        if (isAssignUser) {
          showApiErrorNotification(ASSIGN_OWNER_ERROR, error.message);
        } else {
          showApiErrorNotification(REMOVE_OWNER_ERROR, error.message);
        }
      },
      onSuccess: () => {
        if (isAssignUser) {
          showApiSuccessNotification(ASSIGN_OWNER_SUCCESSFUL, '');
        } else {
          showApiSuccessNotification(REMOVE_OWNER_SUCCESSFUL, '');
        }
      },
    },
  );

  return [
    assignUserMutate,
    isAssignUserLoading,
    {
      data: mutationResult,
    },
  ] as UseMutationHookReturnType<AssignUserResponseType, AssignUserData>;
};

export const useGetSegmentExperiments = (
  isIntPublisher: boolean,
): UseQueryResult<SegmentExperimentResponseData> => {
  const queryResult = useReactQuery<SegmentExperimentResponseData>(
    [GET_SEGMENT_EXPERIMENTS],
    () => {
      return getSegmentExperiments();
    },
    {
      onError: (error) => {
        showApiErrorNotification(GET_SEGMENT_EXPERIMENTS_ERROR, error.message);
      },
      enabled: isIntPublisher,
    },
  );
  return {
    ...queryResult,
  } as UseQueryHookReturnType<UsersListResponseData>;
};

export const useGetPartners = (
  isIntPublisher: boolean,
): UseQueryResult<PartnersListResponseData> => {
  const queryResult = useReactQuery<PartnersListResponseData>(
    [GET_PARTNERS_LIST],
    () => {
      return getPartnersList();
    },
    {
      onError: (error) => {
        showApiErrorNotification(GET_PARTNERS_LIST_ERROR, error.message);
      },
      enabled: isIntPublisher,
    },
  );
  return {
    ...queryResult,
    data: queryResult?.data,
  } as UseQueryHookReturnType<PartnersListResponseData>;
};

export const useGetCities = (
  isIntPublisher: boolean,
): UseQueryResult<CitiesListResponseData> => {
  const queryResult = useReactQuery<CitiesListResponseData>(
    [GET_CITIES_LIST],
    () => {
      return getCitiesList();
    },
    {
      onError: (error) => {
        showApiErrorNotification(GET_CITIES_LIST_ERROR, error.message);
      },
      enabled: isIntPublisher,
    },
  );
  return {
    ...queryResult,
    data: queryResult?.data,
  } as UseQueryHookReturnType<CitiesListResponseData>;
};

export const useGetCityList = (
  params: GetLocationsPayloadType,
): UseQueryResult<GetLocationsResponseData> => {
  const queryResult = useReactQuery<GetLocationsResponseData>(
    [params, GET_CITIES],
    () => {
      return getCityList(params);
    },
    {
      onError: (error) => {
        showApiErrorNotification(GET_CITIES_LIST_ERROR, error.message);
      },
      enabled: params.name !== '',
    },
  );
  return {
    ...queryResult,
    data: queryResult?.data,
  } as UseQueryHookReturnType<GetLocationsResponseData>;
};

export const useGetStateList = (
  params: GetLocationsPayloadType,
): UseQueryResult<GetLocationsResponseData> => {
  const queryResult = useReactQuery<GetLocationsResponseData>(
    [params, GET_STATES],
    () => {
      return getStateList(params);
    },
    {
      onError: (error) => {
        showApiErrorNotification(GET_STATE_LIST_ERROR, error.message);
      },
      enabled: params.name !== '',
    },
  );
  return {
    ...queryResult,
    data: queryResult?.data,
  } as UseQueryHookReturnType<GetLocationsResponseData>;
};

export const useGetSiloCityList = (
  countryCode?: string,
  search?: string,
): UseQueryResult<SiloCityResponseData> => {
  const queryResult = useReactQuery<SiloCityResponseData>(
    [GET_SILO_CITIES, countryCode, search],
    () => getSiloCityList(countryCode, search),
    {
      onError: (error) => {
        showApiErrorNotification(GET_CITIES_LIST_ERROR, error.message);
      },
      // Needs at least one scoping filter; country is the only one we send
      // today. Unscoped, Silo returns cities worldwide — stay idle instead.
      enabled: !!countryCode,
      staleTime: 5 * 60 * 1000,
    },
  );
  return {
    ...queryResult,
    data: queryResult?.data,
  } as UseQueryHookReturnType<SiloCityResponseData>;
};

export const useGetSiloStateList = (
  countryCode?: string,
): UseQueryResult<SiloStateResponseData> => {
  const queryResult = useReactQuery<SiloStateResponseData>(
    [GET_SILO_STATES, countryCode],
    () => getSiloStateList(countryCode),
    {
      onError: (error) => {
        showApiErrorNotification(GET_STATE_LIST_ERROR, error.message);
      },
      // Silo scopes states by country only; unscoped it returns them
      // worldwide, so stay idle instead.
      enabled: !!countryCode,
      staleTime: 5 * 60 * 1000,
    },
  );
  return {
    ...queryResult,
    data: queryResult?.data,
  } as UseQueryHookReturnType<SiloStateResponseData>;
};

// A stored targeting rule keeps only the numeric state id for a city, so callers
// that render those tags need the state name at render time. Passing the same
// country code as the city/state pickers reuses their cached list instead of
// firing another request.
export const useSiloStateNames = (
  countryCode?: string,
): Map<number, string> => {
  const { data: siloStateData } = useGetSiloStateList(countryCode);
  return useMemo(() => {
    const nameById = new Map<number, string>();
    (siloStateData?.results || []).forEach((state) =>
      nameById.set(state.stateId, state.stateName),
    );
    return nameById;
  }, [siloStateData]);
};

export const useGetSiloAppList = (): UseQueryResult<SiloAppResponseData> => {
  const queryResult = useReactQuery<SiloAppResponseData>(
    [GET_SILO_APPS],
    () => getSiloAppList(),
    {
      onError: (error) => {
        showApiErrorNotification(GET_APP_LIST_ERROR, error.message);
      },
      staleTime: 5 * 60 * 1000,
    },
  );
  return {
    ...queryResult,
    data: queryResult?.data,
  } as UseQueryHookReturnType<SiloAppResponseData>;
};

export const useGetRejectReasons = (): UseQueryResult<StringMap> => {
  const queryResult = useReactQuery<StringMap>(
    [GET_REJECT_REASONS],
    () => {
      return getRejectReasons();
    },
    {
      onError: (error) => {
        showApiErrorNotification(GET_REJECT_REASONS_ERROR, error.message);
      },
    },
  );
  return {
    ...queryResult,
    data: queryResult?.data,
  } as UseQueryHookReturnType<StringMap>;
};

export const useGetChannels = (): UseQueryResult<ChannelsResponseData> => {
  const {
    selectedPublisherData: { id },
  } = useContext(SelectedPublisherDataContext);
  const queryResult = useReactQuery<ChannelsResponseData | null>(
    [GET_CHANNELS, id],
    () => {
      if (id) {
        return getChannels(id);
      }
      return null;
    },
    {
      onError: (error) => {
        showApiErrorNotification(GET_CHANNELS_ERROR, error.message);
      },
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );
  return {
    ...queryResult,
    data: queryResult?.data,
  } as UseQueryHookReturnType<ChannelsResponseData>;
};

export const useGetAppConfig = (): UseQueryResult<AppConfigResponseData> => {
  const queryResult = useReactQuery<AppConfigResponseData>(
    [GET_APP_CONFIG],
    () => {
      return getAppConfig();
    },
    {
      onError: (error) => {
        showApiErrorNotification(GET_APP_CONFIG_ERROR, error.message);
      },
    },
  );
  return {
    ...queryResult,
    data: queryResult?.data,
  } as UseQueryHookReturnType<AppConfigResponseData>;
};

export const useGetEuValueProps = (
  euId: string,
): UseQueryResult<ValuePropResponseType> => {
  const queryResult = useReactQuery<ValuePropResponseType>(
    [GET_EU_VALUE_PROP, euId],
    () => {
      return getValueProp(euId);
    },
    {
      onError: (error) => {
        showApiErrorNotification(GET_VALUE_PROP_ERROR, error.message);
      },
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );
  return {
    ...queryResult,
    data: queryResult?.data,
  } as UseQueryHookReturnType<ValuePropResponseType>;
};

export const useGetCardTags = (): UseQueryResult<TagsResponseData> => {
  const queryResult = useReactQuery<TagsResponseData>(
    [GET_CARD_TAGS],
    () => {
      return getCardTags();
    },
    {
      onError: (error) => {
        showApiErrorNotification(GET_TAGS_ERROR, error.message);
      },
      staleTime: Infinity,
    },
  );
  return {
    ...queryResult,
    data: queryResult?.data,
  } as UseQueryHookReturnType<TagsResponseData>;
};
